import { httpClient } from 'providers/clients/httpClient';
import {
  PaymentMethod,
  SpreedlyIframeInitResponse,
  UserWalletResponse,
} from 'utility/types/dataProvider';

// -----------------------------------------------
// ------------ USERWALLETS PROVIDER -------------
// -----------------------------------------------
export const userWalletsProvider = {
  getUserWalletData: async (
    uuid: string,
    { nextToken, limit }: { nextToken?: string | null; limit: number },
  ): Promise<UserWalletResponse> => {
    try {
      const path = `/pay/wallet/${uuid}/search/`;

      const params = new URLSearchParams({
        ...(nextToken && { page: nextToken }),
        limit: limit.toString(),
      });

      const stringPath = `${path}?${params.toString()}`;

      const { data } = await httpClient({
        url: stringPath,
        method: 'GET',
      });

      const result: UserWalletResponse = data?.data;
      return result;
    } catch (error) {
      console.error('[getUserWalletData] Error:', error);
      throw error;
    }
  },

  addPaymentMethod: async (uuid: string, params: any): Promise<UserWalletResponse> => {
    try {
      const path = `/pay/wallet/${uuid}/payment/`;

      const { data } = await httpClient({
        url: path,
        method: 'POST',
        data: JSON.stringify(params),
      });

      const result: UserWalletResponse = data?.data;
      return result;
    } catch (error) {
      console.error('[addPaymentMethod] Error:', error);
      throw error;
    }
  },

  initSpreedlyIframe: async (): Promise<SpreedlyIframeInitResponse> => {
    try {
      const path = '/pay/spreedly/init_iframe/';

      const { data } = await httpClient({
        url: path,
        method: 'POST',
      });

      const result = data?.data ?? data;
      const requiredFields = ['nonce', 'timestamp', 'certificateToken', 'signature'];
      const hasRequiredFields = requiredFields.every(
        (field) => typeof result?.[field] === 'string' && result[field],
      );

      if (!hasRequiredFields) {
        throw new Error('[initSpreedlyIframe] Missing enhanced security fields.');
      }

      return result as SpreedlyIframeInitResponse;
    } catch (error) {
      console.error('[initSpreedlyIframe] Error:', error);
      throw error;
    }
  },

  removePaymentMethod: async (uuid: string, paymentId: string): Promise<void> => {
    try {
      const path = `/pay/wallet/${uuid}/payment/${paymentId}/`;

      await httpClient({
        url: path,
        method: 'DELETE',
      });

      const result = Promise.resolve();
      return result;
    } catch (error) {
      console.error('[removePaymentMethod] Error:', error);
      throw error;
    }
  },

  editPaymentMethod: async (
    uuid: string,
    paymentId: string,
    params: any,
  ): Promise<void> => {
    try {
      const path = `/pay/wallet/${uuid}/payment/${paymentId}/`;

      await httpClient({
        url: path,
        method: 'PUT',
        data: JSON.stringify(params),
      });

      const result = Promise.resolve();
      return result;
    } catch (error) {
      console.error('[editPaymentMethod] Error:', error);
      throw error;
    }
  },

  getPaymentMethodByToken: async (
    uuid: string,
    paymentToken: string,
  ): Promise<PaymentMethod | null> => {
    try {
      if (!uuid || !paymentToken) {
        throw new Error('User id and payment token are required');
      }

      const path = `/pay/wallet/${uuid}/payment/${paymentToken}/`;

      const response = await httpClient({
        url: path,
        method: 'GET',
        validateStatus: (status) => {
          const isOk = status >= 200 && status < 300;
          return isOk || status === 404 || status === 410;
        },
      });

      if (response.status === 404 || response.status === 410) {
        return null;
      }

      const result = response.data?.data as PaymentMethod | undefined;

      return result ?? null;
    } catch (error) {
      const status = (error as any)?.response?.status ?? (error as any)?.status;
      if (status === 404 || status === 410) {
        return null;
      }

      console.error('[getPaymentMethodByToken] Error:', error);
      throw error;
    }
  },
};
